import { g, helpers } from "../../util/index.ts";
import genContract from "../player/genContract.ts";
import type { PlayerWithoutKey } from "../../../common/types.ts";

// A good young player who lingers unsigned in free agency shouldn't sit at a max ask that no team
// can fit. In reality he eventually caves to a short "prove-it" deal at roughly half of what his
// value would command, and his previous team (Bird rights) is the natural place he lands -- even
// over the cap. These knobs govern that fallback, shared by decreaseDemands (the demand collapse)
// and resolveLingeringFreeAgents (the incumbent signing).

// Fraction of a player's value-based ask that a prove-it deal lands at.
export const PROVE_IT_DISCOUNT = 0.5;

// How long a free agent holds out (days unsigned) before he starts caving toward a prove-it deal.
// numDaysFreeAgent increments once per free-agency day and once per regular-season game.
export const PROVE_IT_PATIENCE_DAYS = 30;

// Per-day fraction of the remaining gap to the prove-it floor that a stuck free agent gives up, so
// the collapse is quick but not instant once his patience runs out.
const PROVE_IT_COLLAPSE_RATE = 0.34;

// The dollar amount a lingering free agent caves to: ~half his value-based ask, floored at the
// league minimum. genContract(p, false) is the deterministic value->salary mapping (no random
// noise), so this is stable day to day and doesn't ratchet down as his current ask decays.
export const proveItAmount = (p: PlayerWithoutKey) => {
	const minContract = g.get("minContract");
	const valueAsk = genContract(p, false).amount;
	return Math.max(
		minContract,
		helpers.roundContract(PROVE_IT_DISCOUNT * valueAsk),
	);
};

// A prove-it deal is only worth forcing for players who'd command a real (non-min) contract. If
// half a player's value-ask is barely above the minimum, he's replacement level and the normal
// min-contract market already handles him -- no incumbent safety net needed.
export const proveItWorthKeeping = (amount: number) =>
	amount > g.get("minContract") * 1.75;

// Has this free agent held out long enough that he should start caving? True once he's been
// unsigned beyond the patience window, or -- more decisively -- has already sat unsigned through a
// prior season.
export const proveItStuck = (p: PlayerWithoutKey) =>
	p.yearsFreeAgent > 0 || p.numDaysFreeAgent >= PROVE_IT_PATIENCE_DAYS;

// One day's step of a stuck free agent's ask toward his prove-it floor. Returns the new amount.
export const proveItCollapse = (currentAmount: number, floor: number) => {
	if (currentAmount <= floor) {
		return currentAmount;
	}
	const next =
		currentAmount - PROVE_IT_COLLAPSE_RATE * (currentAmount - floor);
	return Math.max(floor, helpers.roundContract(next));
};

// The player's last real (non-free-agent) team -- where his Bird rights sit. Reads the most recent
// stats row with a real tid; falls back to his draft team. undefined if neither exists (e.g. an
// undrafted player who has never been on a roster).
export const getLastRealTid = (p: PlayerWithoutKey): number | undefined => {
	for (let i = p.stats.length - 1; i >= 0; i--) {
		const tid = p.stats[i]?.tid;
		if (tid !== undefined && tid >= 0) {
			return tid;
		}
	}
	if (p.draft.tid >= 0) {
		return p.draft.tid;
	}
	return undefined;
};
